// src/pages/Settings/communication/emailConfig.js
import React, { useState, useEffect } from "react";
import { get, post } from 'helpers/api_helper';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
  Badge
} from 'reactstrap';
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { showSuccessAlert, showErroeAlert } from "pages/utils/alertMessages";
import cookieHelper from "helpers/getCookieData";
import {
  RiServerLine,
  RiShieldCheckLine,
  RiSendPlaneLine,
  RiGlobalLine,
  RiBuildingLine,
  RiRefreshLine,
  RiDeleteBinLine,
  RiMailCheckLine,
} from "react-icons/ri";

const EmailConfig = () => {
  const { t } = useTranslation();
  const clinic = cookieHelper.getCookie('_c') ? JSON.parse(atob(cookieHelper.getCookie('_c'))) : null;
  const clinicId = clinic?.id || 8;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState(clinic?.email || 'doctor@oralstop.com');

  const [smtpServer, setSmtpServer] = useState({
    clinic_id: clinicId,
    smtp: 0,
    has_custom_smtp: false,
    active_source: 'GLOBAL',
    fallback_global_available: true,
    host: '',
    port: 587,
    encription: 'ssl',
    from_email: '',
    username: '',
    password: '',
    has_smtp_password: false,
  });

  const fetchClinicSmtp = async () => {
    setLoading(true);
    try {
      const res = await get(`/clinics/${clinicId}/smtp-config`);
      if (res?.success && res?.body) {
        const d = res.body;
        setSmtpServer({
          clinic_id: clinicId,
          smtp: d.smtp_active ? 1 : 0,
          has_custom_smtp: d.has_custom_smtp,
          active_source: d.active_source || 'GLOBAL',
          fallback_global_available: d.fallback_global_available,
          host: d.smtp_host || '',
          port: d.smtp_port || 587,
          encription: d.smtp_encription || 'ssl',
          from_email: d.smtp_from_email || '',
          username: d.smtp_username || '',
          password: '',
          has_smtp_password: d.has_smtp_password,
        });
        if (d.smtp_from_email) {
          setTestEmail(d.smtp_from_email);
        }
      }
    } catch (err) {
      console.warn("Notice loading clinic SMTP:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClinicSmtp();
  }, [clinicId]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        smtp: 1,
        smtp_host: smtpServer.host,
        smtp_port: smtpServer.port,
        smtp_encription: smtpServer.encription,
        smtp_from_email: smtpServer.from_email,
        smtp_username: smtpServer.username,
      };
      if (smtpServer.password && !smtpServer.password.includes('•••')) {
        payload.smtp_password = smtpServer.password;
      }

      const res = await post(`/clinics/${clinicId}/smtp-config`, payload);
      if (res?.success) {
        showSuccessAlert(t("Custom SMTP settings saved successfully!"));
        fetchClinicSmtp();
      } else {
        showErroeAlert(res?.message || t("Failed to save SMTP settings."));
      }
    } catch (err) {
      showErroeAlert(t("Error saving SMTP: ") + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetToGlobal = async () => {
    if (!window.confirm(t("Are you sure you want to clear custom SMTP credentials and use the Super Admin Global SMTP default?"))) {
      return;
    }
    setSaving(true);
    try {
      const res = await post(`/clinics/${clinicId}/smtp-config`, { reset_to_global: true });
      if (res?.success) {
        showSuccessAlert(t("Reverted to Platform Global SMTP successfully!"));
        fetchClinicSmtp();
      }
    } catch (err) {
      showErroeAlert(t("Failed to reset: ") + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      showErroeAlert(t("Please enter a test recipient email."));
      return;
    }
    setTesting(true);
    try {
      const res = await post(`/clinics/${clinicId}/test-smtp`, { to_email: testEmail });
      if (res?.success) {
        showSuccessAlert(t("Test email dispatched successfully to ") + testEmail);
      } else {
        showErroeAlert(res?.message || t("Failed to send test email."));
      }
    } catch (err) {
      showErroeAlert(t("Test dispatch error: ") + err.message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="py-5 text-center">
        <Spinner color="primary" size="md" />
        <p className="mt-2 text-muted font-size-13">{t("Loading SMTP Server configuration...")}</p>
      </div>
    );
  }

  return (
    <div className="email-config-container">
      {/* Credential Resolution Status Banner */}
      <Card className="border shadow-sm mb-4 bg-light">
        <CardBody className="py-3 px-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <div className="rounded-circle p-2 bg-primary text-white font-size-20 d-flex align-items-center justify-content-center" style={{ width: 44, height: 44 }}>
                <RiServerLine />
              </div>
              <div>
                <h6 className="font-weight-bold text-dark mb-0 d-flex align-items-center gap-2">
                  {t("Outgoing Mail Server Resolution")}
                  {smtpServer.has_custom_smtp ? (
                    <Badge color="info" className="font-size-11">
                      <RiBuildingLine className="me-1" /> {t("Active: Custom Clinic SMTP")}
                    </Badge>
                  ) : (
                    <Badge color="primary" className="font-size-11">
                      <RiGlobalLine className="me-1" /> {t("Active: Platform Global SMTP (Default)")}
                    </Badge>
                  )}
                </h6>
                <p className="text-muted font-size-12 mb-0">
                  {smtpServer.has_custom_smtp
                    ? t("Your clinic uses its own private SMTP server. Email notifications and reminders will be delivered from your custom domain.")
                    : t("No custom SMTP configured. Outgoing communications automatically resolve to the Super Admin's high-deliverability Global SMTP infrastructure.")}
                </p>
              </div>
            </div>

            {smtpServer.has_custom_smtp && (
              <Button
                color="outline-danger"
                size="sm"
                onClick={handleResetToGlobal}
                disabled={saving}
                className="font-size-12"
              >
                <RiDeleteBinLine className="me-1" /> {t("Reset to Global SMTP")}
              </Button>
            )}
          </div>
        </CardBody>
      </Card>

      <Row>
        <Col lg={8}>
          <Card className="border shadow-sm">
            <CardHeader className="bg-white border-bottom py-3">
              <h5 className="font-weight-bold mb-0 text-dark d-flex align-items-center gap-2">
                <RiServerLine className="text-primary font-size-18" />
                {t("Custom Clinic SMTP Server Settings")}
              </h5>
              <small className="text-muted">
                {t("Enter your clinic's email credentials (Google Workspace, Microsoft 365, Zoho, or cPanel SMTP).")}
              </small>
            </CardHeader>
            <CardBody className="p-4">
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="smtpServer" className="font-weight-semibold font-size-12">{t("SMTP Server Host *")}</Label>
                      <Input
                        type="text"
                        id="smtpServer"
                        placeholder={t("e.g. smtp.gmail.com or mail.yourclinic.com")}
                        value={smtpServer?.host}
                        onChange={(e) => setSmtpServer((p) => ({ ...p, host: e.target.value }))}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label for="port" className="font-weight-semibold font-size-12">{t("Port *")}</Label>
                      <Input
                        type="number"
                        id="port"
                        placeholder="587 or 465"
                        value={smtpServer?.port}
                        onChange={(e) => setSmtpServer((p) => ({ ...p, port: e.target.value }))}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label for="encryption" className="font-weight-semibold font-size-12">{t("Encryption *")}</Label>
                      <Select
                        id="encryption"
                        value={{ value: smtpServer?.encription, label: (smtpServer?.encription || 'SSL').toUpperCase() }}
                        options={[
                          { value: 'ssl', label: 'SSL' },
                          { value: 'tls', label: 'TLS' },
                        ]}
                        onChange={(opt) => setSmtpServer((p) => ({ ...p, encription: opt.value }))}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row className="mt-2">
                  <Col md={4}>
                    <FormGroup>
                      <Label for="from_email" className="font-weight-semibold font-size-12">{t("From Email Address *")}</Label>
                      <Input
                        type="email"
                        id="from_email"
                        placeholder={t("e.g. info@yourclinic.com")}
                        value={smtpServer?.from_email}
                        onChange={(e) => setSmtpServer((p) => ({ ...p, from_email: e.target.value }))}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="username" className="font-weight-semibold font-size-12">{t("SMTP Username *")}</Label>
                      <Input
                        type="text"
                        id="username"
                        placeholder={t("Enter SMTP Username / Email")}
                        value={smtpServer?.username}
                        onChange={(e) => setSmtpServer((p) => ({ ...p, username: e.target.value }))}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="password" className="font-weight-semibold font-size-12">
                        {t("SMTP Password *")} {smtpServer.has_smtp_password && <span className="text-success font-size-11 font-weight-normal">({t("Saved")})</span>}
                      </Label>
                      <Input
                        type="password"
                        id="password"
                        placeholder={smtpServer.has_smtp_password ? "••••••••" : t("Enter App Password")}
                        value={smtpServer?.password}
                        onChange={(e) => setSmtpServer((p) => ({ ...p, password: e.target.value }))}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                  <Button
                    type="button"
                    color="light"
                    onClick={fetchClinicSmtp}
                    disabled={saving}
                  >
                    <RiRefreshLine className="me-1" /> {t("Reset")}
                  </Button>
                  <Button
                    color="primary"
                    type="submit"
                    disabled={saving}
                    className="px-4"
                  >
                    {saving ? <Spinner size="sm" className="me-1" /> : <RiShieldCheckLine className="me-1" />}
                    {t("Save SMTP Settings")}
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>

        {/* Test Email Card */}
        <Col lg={4}>
          <Card className="border shadow-sm bg-light">
            <CardHeader className="bg-white border-bottom py-3">
              <h6 className="font-weight-bold mb-0 text-dark d-flex align-items-center gap-1.5">
                <RiMailCheckLine className="text-primary font-size-18" />
                {t("Verify SMTP Connection")}
              </h6>
              <small className="text-muted">{t("Send a test message to ensure deliverability")}</small>
            </CardHeader>
            <CardBody className="p-3">
              <FormGroup>
                <Label className="font-weight-semibold font-size-12">{t("Test Recipient Email")}</Label>
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="e.g. doctor@oralstop.com"
                />
              </FormGroup>
              <Button
                color="primary"
                block
                onClick={handleSendTestEmail}
                disabled={testing}
                className="mt-3"
              >
                {testing ? (
                  <>
                    <Spinner size="sm" className="me-1" /> {t("Sending Test...")}
                  </>
                ) : (
                  <>
                    <RiSendPlaneLine className="me-1" /> {t("Dispatch Test Email")}
                  </>
                )}
              </Button>
              <p className="text-muted font-size-11 mt-3 mb-0">
                {t("The test message will be sent through your resolved email provider to verify credentials and TLS handshakes.")}
              </p>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default EmailConfig;